import { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AuthSync } from './components/auth/AuthSync';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/ui/toast';
import { InlineCode } from './components/ui/code-block';
import { ProjectsProvider } from './contexts/ProjectsContext';
import { LoginPage, SignupPage, ProjectsPage, ProjectOverviewPage, ProjectSettingsPage, SecretsPage, SettingsPage, EnvironmentComparisonPage, AuditLogPage, TokensPage, TeamPage, BillingPage, IntegrationsPage, ScansPage, ScanDetailPage, InvitePage, AdminPage, CLICallbackPage } from './pages';
import { setTokenGetter, clearTokenGetter } from './api/client';

// Check if we're in dev mode (bypasses Clerk)
const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';
const devServiceToken = import.meta.env.VITE_DEV_SERVICE_TOKEN || '';

// Get Clerk publishable key from environment (only required if not in dev mode)
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Set up dev mode token getter SYNCHRONOUSLY (before any components render)
// This prevents race conditions where useQuery fires before useEffect runs
if (isDevMode && devServiceToken) {
  setTokenGetter(async () => devServiceToken);
  console.log('[Dev Mode] Token getter initialized');
} else if (isDevMode) {
  console.warn('[Dev Mode] No VITE_DEV_SERVICE_TOKEN set - API calls will fail');
}

function ConfigurationError() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md text-center">
        <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-4">Configuration Error</h1>
        <p className="text-muted-foreground mb-4">
          Missing required environment variable:{' '}
          <InlineCode>VITE_CLERK_PUBLISHABLE_KEY</InlineCode>
        </p>
        <p className="text-muted-foreground/70 text-sm">
          For local development without Clerk, set{' '}
          <InlineCode>VITE_DEV_MODE=true</InlineCode> in your .env file.
        </p>
      </div>
    </div>
  );
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry on 429 (rate limit) or 4xx client errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status === 429 || (status >= 400 && status < 500)) {
            return false;
          }
        }
        return failureCount < 1;
      },
    },
  },
});

// =============================================================================
// Dev Mode Auth Context (bypasses Clerk for local development)
// =============================================================================

interface DevAuthContextType {
  isSignedIn: boolean;
  isLoaded: boolean;
  userId: string | null;
  getToken: () => Promise<string | null>;
}

const DevAuthContext = createContext<DevAuthContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useDevAuth(): DevAuthContextType {
  const ctx = useContext(DevAuthContext);
  if (!ctx) {
    throw new Error('useDevAuth must be used within DevAuthProvider');
  }
  return ctx;
}

/**
 * Dev mode auth provider - provides mock auth when VITE_DEV_MODE=true
 */
function DevAuthProvider({ children }: { children: React.ReactNode }) {
  const value: DevAuthContextType = {
    isSignedIn: true,
    isLoaded: true,
    userId: 'dev-user',
    getToken: async () => devServiceToken,
  };

  return (
    <DevAuthContext.Provider value={value}>
      {children}
    </DevAuthContext.Provider>
  );
}

function DevApiAuthSetup({ children }: { children: React.ReactNode }) {
  return (
    <AuthStateContext.Provider value={{ isAuthenticated: true }}>
      {children}
    </AuthStateContext.Provider>
  );
}

/**
 * Dev mode protected route - always allows access in dev mode
 */
function DevProtectedRoute({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

/**
 * Dev mode auth sync - no-op in dev mode
 */
function DevAuthSync({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

// Auth state context for sharing auth status with data providers
const AuthStateContext = createContext<{ isAuthenticated: boolean }>({ isAuthenticated: false });

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthState() {
  return useContext(AuthStateContext);
}

function ApiAuthSetup({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const [isTokenSetterReady, setIsTokenSetterReady] = useState(false);

  // Set Sentry user context when authenticated
  useEffect(() => {
    if (isUserLoaded && user) {
      Sentry.setUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
      });
    } else if (isUserLoaded && !user) {
      Sentry.setUser(null);
    }
  }, [isUserLoaded, user]);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn) {
      setTokenGetter(getToken);
    } else {
      clearTokenGetter();
    }
    // This setState is intentional - we need to signal that token setup is complete
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsTokenSetterReady(true);
  }, [isLoaded, isSignedIn, getToken]);

  if (!isTokenSetterReady) {
    return null;
  }

  return (
    <AuthStateContext.Provider value={{ isAuthenticated: !!isSignedIn }}>
      {children}
    </AuthStateContext.Provider>
  );
}

/**
 * App with Clerk authentication (production mode)
 * Note: ClerkProvider is in main.tsx per Clerk's recommended pattern
 */
function ClerkApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <ApiAuthSetup>
            <ProjectsProvider>
              <AuthSync>
                <Routes>
                  {/* Public routes with simple layout */}
                  <Route path="/" element={<Layout />}>
                    <Route path="login" element={<LoginPage />} />
                    <Route path="signup" element={<SignupPage />} />
                  </Route>

                  {/* Invitation page (semi-public - shows details without auth, accept requires auth) */}
                  <Route path="/invite/:token" element={<InvitePage />} />

                  {/* CLI OAuth callback page (handles its own auth via RedirectToSignIn) */}
                  <Route path="/cli/callback" element={<CLICallbackPage />} />

                  {/* Protected routes with app shell */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <AppShell />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<ProjectsPage />} />
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="projects/:projectId" element={<ProjectOverviewPage />} />
                    <Route path="projects/:projectId/settings" element={<ProjectSettingsPage />} />
                    <Route path="projects/:projectId/secrets" element={<SecretsPage />} />
                    <Route path="projects/:projectId/compare" element={<EnvironmentComparisonPage />} />
                    <Route path="projects/:projectId/audit" element={<AuditLogPage />} />
                    <Route path="projects/:projectId/team" element={<TeamPage />} />
                    <Route path="projects/:projectId/integrations" element={<IntegrationsPage />} />
                    <Route path="projects/:projectId/scans" element={<ScansPage />} />
                    <Route path="projects/:projectId/scans/:scanId" element={<ScanDetailPage />} />
                    <Route path="projects/:projectId/tokens" element={<TokensPage />} />
                    <Route path="tokens" element={<TokensPage />} />
                    <Route path="billing" element={<BillingPage />} />
                    <Route path="billing/success" element={<BillingPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="admin" element={<AdminPage />} />
                  </Route>
                </Routes>
              </AuthSync>
            </ProjectsProvider>
          </ApiAuthSetup>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

/**
 * App with dev mode authentication (local development)
 */
function DevApp() {
  return (
    <DevAuthProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <DevApiAuthSetup>
            <ProjectsProvider>
              <DevAuthSync>
                <BrowserRouter>
                  <Routes>
                    {/* Public routes with simple layout */}
                    <Route path="/" element={<Layout />}>
                      <Route path="login" element={<LoginPage />} />
                      <Route path="signup" element={<SignupPage />} />
                    </Route>

                    {/* Invitation page */}
                    <Route path="/invite/:token" element={<InvitePage />} />

                    {/* Protected routes with app shell - no protection in dev mode */}
                    <Route
                      path="/"
                      element={
                        <DevProtectedRoute>
                          <AppShell />
                        </DevProtectedRoute>
                      }
                    >
                      <Route index element={<ProjectsPage />} />
                      <Route path="projects" element={<ProjectsPage />} />
                      <Route path="projects/:projectId" element={<ProjectOverviewPage />} />
                      <Route path="projects/:projectId/settings" element={<ProjectSettingsPage />} />
                      <Route path="projects/:projectId/secrets" element={<SecretsPage />} />
                      <Route path="projects/:projectId/compare" element={<EnvironmentComparisonPage />} />
                      <Route path="projects/:projectId/audit" element={<AuditLogPage />} />
                      <Route path="projects/:projectId/team" element={<TeamPage />} />
                      <Route path="projects/:projectId/integrations" element={<IntegrationsPage />} />
                      <Route path="projects/:projectId/scans" element={<ScansPage />} />
                      <Route path="projects/:projectId/scans/:scanId" element={<ScanDetailPage />} />
                      <Route path="projects/:projectId/tokens" element={<TokensPage />} />
                      <Route path="tokens" element={<TokensPage />} />
                      <Route path="billing" element={<BillingPage />} />
                      <Route path="billing/success" element={<BillingPage />} />
                      <Route path="settings" element={<SettingsPage />} />
                      <Route path="admin" element={<AdminPage />} />
                    </Route>
                  </Routes>
                </BrowserRouter>
              </DevAuthSync>
            </ProjectsProvider>
          </DevApiAuthSetup>
        </ToastProvider>
      </QueryClientProvider>
    </DevAuthProvider>
  );
}

function App() {
  if (isDevMode) {
    console.log('[Dev Mode] Running without Clerk authentication');
    return <DevApp />;
  }
  if (!clerkPubKey) {
    return <ConfigurationError />;
  }
  return <ClerkApp />;
}

export default App;
